import type { UserInfo } from 'types/userInfo';
import { BASE_REST_API_URL } from 'common/constants';
import { BaseApiClient } from './baseApiClient';
import { UsersApiClient } from './usersApi';

export interface Credentials {
  identifier: string;
  password: string;
}

export type LoginResult = { jwt: string; user: UserInfo };
export type RenewTokenResult = { jwt: string };

/**
 * @deprecated Use class instead because there is only one implementation of this interface which makes searching for references
 * more difficult, name shouldn't be prefixed too: https://blog.nette.org/en/prefixes-and-suffixes-do-not-belong-in-interface-names.
 */
export interface IAuthApiClient {
  login: (credentials: Credentials) => Promise<LoginResult>;
  renewToken: (signal: AbortSignal) => Promise<RenewTokenResult>;
}

export class AuthApiClient extends BaseApiClient implements IAuthApiClient {
  constructor(jwt: string) {
    super('auth', BASE_REST_API_URL, jwt);
  }

  async login(credentials: Credentials): Promise<LoginResult> {
    const { data } = await this.axios.post<LoginResult>(`/local`, credentials, {
      withCredentials: true,
      headers: {
        Authorization: undefined,
      },
    });

    if (!data) {
      throw new Error('Invalid response from POST /auth/local');
    }

    const userApiClient = new UsersApiClient(data.jwt);
    const user = await userApiClient.findById(data.user.id, { populate: ['role', 'reseller'] });

    if (!user) {
      throw new Error('Invalid response from GET /users');
    }

    return { ...data, user };
  }

  async renewToken(signal: AbortSignal): Promise<RenewTokenResult> {
    const response = await this.axios.post<RenewTokenResult>(
      `/local/token`,
      {},
      {
        withCredentials: true,
        responseType: 'json',
        signal,
        headers: {
          Authorization: undefined,
        },
      },
    );

    return response.data;
  }
}
